import { LiveUpdate } from '@capawesome/capacitor-live-update';
import { isPlatform } from '@ionic/react';
import { toast } from 'sonner';

export class LiveUpdateService {
  private static instance: LiveUpdateService;

  private constructor() {
    // Private constructor to enforce singleton pattern
  }

  public static getInstance(): LiveUpdateService {
    if (!LiveUpdateService.instance) {
      LiveUpdateService.instance = new LiveUpdateService();
    }
    return LiveUpdateService.instance;
  }

  /**
   * Check for available updates
   * @returns Promise with update info or null if no update is available
   */
  public async checkForUpdate(): Promise<{ version: string; url: string } | null> {
    if (!isPlatform('capacitor')) {
      console.log('Live Update is only available on native platforms');
      return null;
    }

    try {
      // Get the current app version for logging purposes
      const versionResult = await LiveUpdate.getVersionName();
      console.log(`Current app version: ${versionResult.versionName}`);
      
      // Fetch the latest bundle information
      const result = await LiveUpdate.fetchLatestBundle();
      
      if (result.bundleId && result.downloadUrl) {
        // If there's a new bundle available
        const currentBundle = await LiveUpdate.getCurrentBundle();
        
        // Check if the latest bundle is different from the current one
        if (result.bundleId !== currentBundle.bundleId) {
          return { 
            version: result.bundleId, 
            url: result.downloadUrl 
          };
        }
      }
      return null;
    } catch (error) {
      console.error('Error checking for update:', error);
      return null;
    }
  }

  /**
   * Download and apply an update
   * @param url URL of the update package
   * @returns Promise resolving to boolean indicating success
   */
  public async downloadAndApplyUpdate(url: string): Promise<boolean> {
    if (!isPlatform('capacitor')) {
      console.log('Live Update is only available on native platforms');
      return false;
    }

    try {
      // Show toast notification
      toast.info('Downloading update...', {
        duration: 3000,
      });

      // Generate a bundle ID based on timestamp
      const bundleId = `update-${Date.now()}`;
      
      // Download the bundle
      await LiveUpdate.downloadBundle({
        url: url,
        bundleId: bundleId
      });
      
      toast.success('Update downloaded, applying now...', {
        duration: 3000,
      });
      
      // Set the downloaded bundle as the next bundle to use
      await LiveUpdate.setNextBundle({
        bundleId: bundleId
      });
      
      // Reload the app to apply the new bundle
      await LiveUpdate.reload();
      
      toast.success('Update applied successfully!', {
        duration: 3000,
      });
      return true;
    } catch (error) {
      console.error('Error downloading or applying update:', error);
      toast.error('Update failed', {
        duration: 3000,
      });
      return false;
    }
  }

  /**
   * Check for updates and apply them automatically if available
   */
  public async checkAndApplyUpdate(): Promise<void> {
    const update = await this.checkForUpdate();
    if (update) {
      toast.info(`Update ${update.version} available`, {
        duration: 5000,
        action: {
          label: 'Update Now',
          onClick: () => this.downloadAndApplyUpdate(update.url),
        },
      });
    } else {
      // Notify the plugin that the app is ready to use
      await LiveUpdate.ready();
    }
  }

  /**
   * Reset the app to the bundled version
   */
  public async resetToBundle(): Promise<boolean> {
    if (!isPlatform('capacitor')) {
      console.log('Live Update is only available on native platforms');
      return false;
    }

    try {
      // Reset to the default bundle
      await LiveUpdate.reset();
      
      // Reload the app to apply the changes
      await LiveUpdate.reload();
      return true;
    } catch (error) {
      console.error('Error resetting to bundle:', error);
      return false;
    }
  }
}

export default LiveUpdateService.getInstance();
